import React, { useState } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import StyledPagination from "./StyledPagination";
import { NEUTRAL, RADIUS } from "../styles/brandTokens";

function IntegrationTable({
  name,
  logo,
  actions,
  enabled,
  onToggle,
  onPermissionUpdate,
  customerId,
  applicationId,
  disableEdit = false,
}: {
  name: string;
  logo: string;
  actions: any[];
  enabled: boolean;
  onToggle: () => void;
  onPermissionUpdate?: (params: {
    customerId: string;
    applicationId: string;
    actionName: string;
    permission: 'auto' | 'approval' | 'off';
  }) => Promise<void>;
  customerId: string;
  applicationId: string;
  disableEdit?: boolean;
}) {
  const [updatingActionId, setUpdatingActionId] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'auto' | 'approval' | 'never'>('all');

  const stickyHeaderCellStyle = {
    backgroundColor: NEUTRAL.subtle,
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: "0.04em",
    fontFamily: "var(--theo-font-sans)",
    color: NEUTRAL.textSecondary,
    paddingTop: 8,
    paddingBottom: 8,
    borderBottom: `1px solid ${NEUTRAL.border}`,
  };

  /** Dense body cells: the matrix is scanned down a column, not read row by row. */
  const bodyCellSx = {
    fontFamily: "var(--theo-font-sans)",
    fontSize: 13,
    color: NEUTRAL.text,
    py: 0.75,
    borderBottom: `1px solid ${NEUTRAL.border}`,
  };

  const handlePermissionChange = async (actionId: string, newPermission: 'auto' | 'approval' | 'never') => {
    if (!onPermissionUpdate) return;
    setUpdatingActionId(actionId);
    try {
      const apiPermission: 'auto' | 'approval' | 'off' =
        newPermission === 'never' ? 'off' : newPermission;
      const action = actions.find(a => a.id === actionId);
      if (!action) throw new Error('Action not found');
      await onPermissionUpdate({
        customerId,
        applicationId,
        actionName: action.name,
        permission: apiPermission
      });
    } catch (error) {
    } finally {
      setUpdatingActionId(null);
    }
  };

  // Filter actions based on selected filter mode
  const filteredActions = actions.filter(action => {
    if (filterMode === 'all') return true;
    if (filterMode === 'auto') return action.permission === 'auto';
    if (filterMode === 'approval') return action.permission === 'approval';
    if (filterMode === 'never') return action.permission === 'off';
    return true;
  });

  return (
    <Card style={{ border: "none", boxShadow: "none" }}>
      <CardContent>
        {/* Filter Toggle */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: 14, 
              fontWeight: 500, 
              fontFamily: "var(--theo-font-sans)",
              color: "#666666"
            }}
          >
            Filter by permission:
          </Typography>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(event, newFilter) => {
              if (newFilter !== null) {
                setFilterMode(newFilter);
              }
            }}
            size="small"
            sx={{
              background: "#fff",
              borderRadius: `${RADIUS.control}px`,
              border: `1px solid ${NEUTRAL.border}`,
              boxShadow: "none",
              '& .MuiToggleButton-root': {
                fontWeight: 500,
                fontSize: '12px',
                fontFamily: "var(--theo-font-sans)",
                px: 1.5,
                py: 0.3,
                textTransform: "none",
                border: "none",
                '&.Mui-selected': {
                  backgroundColor: "#9B7AEF",
                  color: "#fff",
                  '&:hover': {
                    backgroundColor: "#8F6ED5",
                  },
                },
                '&:hover': {
                  backgroundColor: "#F5F5F5",
                },
              },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="auto">Auto</ToggleButton>
            <ToggleButton value="approval">Approval</ToggleButton>
            <ToggleButton value="never">Never</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <div style={{ border: `1px solid ${NEUTRAL.border}`, borderRadius: `${RADIUS.control}px`, overflow: "hidden" }}>
          <div style={{ height: 650, overflowY: "auto" }}>
            {/* size="small" + the cell overrides below: this is a dense permission
                matrix, not a content table; default MUI row padding made every
                row read like a card. */}
            <Table size="small" style={{ backgroundColor: "#FFFFFF" }}>
              <TableHead>
                <TableRow>
                  <TableCell style={stickyHeaderCellStyle}>Allowed Actions</TableCell>
                  <TableCell align="center" style={stickyHeaderCellStyle}>Description</TableCell>
                  <TableCell align="center" style={stickyHeaderCellStyle}>Created</TableCell>
                  <TableCell align="center" style={stickyHeaderCellStyle}>Status</TableCell>
                  <TableCell align="center" style={{...stickyHeaderCellStyle, width: 1}}>Enable</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredActions.map((action, index) => {
                  const isOverride = action.is_override === true;
                  const baselinePermission = action.baseline_permission;
                  
                  return (
                  <TableRow key={index}>
                    <TableCell sx={bodyCellSx}>{action.name}</TableCell>
                    <TableCell align="center" sx={bodyCellSx}>{action.description}</TableCell>
                    <TableCell align="center" sx={bodyCellSx}>{action.created}</TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Chip
                          label={isOverride ? "Override" : "Baseline"}
                          size="small"
                          sx={{
                            backgroundColor: isOverride ? "#FFF4E6" : "#E8F5E9",
                            color: isOverride ? "#E65100" : "#2E7D32",
                            fontWeight: 600,
                            fontSize: '11px',
                            fontFamily: "var(--theo-font-sans)",
                            height: '24px',
                            border: isOverride ? "1px solid #FFB74D" : "1px solid #81C784",
                          }}
                        />
                        {isOverride && baselinePermission && (
                          <Chip
                            label={`Baseline: ${baselinePermission === 'off' ? 'Never' : baselinePermission === 'auto' ? 'Auto' : 'Approval'}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: "#666666",
                              fontWeight: 500,
                              fontSize: '10px',
                              fontFamily: "var(--theo-font-sans)",
                              height: '24px',
                              borderColor: NEUTRAL.border,
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      <ToggleButtonGroup
                        value={action.permission}
                        exclusive
                        size="small"
                        onChange={(event, newPermission) => {
                          if (
                            newPermission !== null &&
                            newPermission !== action.permission
                          ) {
                            handlePermissionChange(
                              action.id,
                              newPermission as 'auto' | 'approval' | 'never'
                            );
                          }
                        }}
                        disabled={updatingActionId === action.id || disableEdit}
                        sx={{
                          background: "#fff",
                          borderRadius: `${RADIUS.control}px`,
                          border: `1px solid ${NEUTRAL.border}`,
                          boxShadow: "none",
                          '& .MuiToggleButton-root': {
                            fontWeight: 600,
                            fontSize: '12px',
                            fontFamily: "var(--theo-font-sans)",
                            px: 1.25,
                            py: 0.35,
                            lineHeight: 1.4,
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                          },
                          '& .MuiSvgIcon-root': { fontSize: 15 },
                        }}
                      >
                        <ToggleButton
                          value="auto"
                          selected={action.permission === "auto"}
                          sx={{
                            color: action.permission === "auto" ? "#9B7AEF" : "#C7C7C7",
                            fontWeight: 700,
                            '& .MuiSvgIcon-root': {
                              color: action.permission === "auto" ? "#9B7AEF" : "#C7C7C7",
                              fontSize: 15,
                            },
                            '&.Mui-selected': {
                              color: "#9B7AEF",
                              backgroundColor: "#FFFFFF",
                            },
                          }}
                        >
                          <CheckIcon />
                          Auto
                        </ToggleButton>
                        <ToggleButton
                          value="approval"
                          selected={action.permission === "approval"}
                          sx={{
                            color: action.permission === "approval" ? "#F0A55E" : "#C7C7C7",
                            fontWeight: action.permission === "approval" ? 700 : 400,
                            '& .MuiSvgIcon-root': {
                              color: action.permission === "approval" ? "#F0A55E" : "#C7C7C7",
                              fontSize: 15,
                            },
                            '&.Mui-selected': {
                              color: "#F0A55E",
                              backgroundColor: "#FFFFFF",
                              borderLeft: "1px solid rgba(0, 0, 0, 0.12) !important",
                            },
                          }}
                        >
                          <HelpOutlineIcon />
                          Approval
                        </ToggleButton>
                        <ToggleButton
                          value="never"
                          selected={action.permission === "off"}
                          sx={{
                            color: action.permission === "off" ? "#222B45" : "#C7C7C7",
                            fontWeight: action.permission === "off" ? 700 : 400,
                            '& .MuiSvgIcon-root': {
                              color: action.permission === "off" ? "#6E6E6E" : "#C7C7C7",
                              fontSize: 15,
                            },
                            '&.Mui-selected': {
                              color: "#6E6E6E",
                              backgroundColor: "#FFFFFF",
                              borderLeft: "1px solid rgba(0, 0, 0, 0.12) !important",
                            },
                          }}
                        >
                          <CloseIcon />
                          Never
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default IntegrationTable;